import React, {
  createElement,
  useRef,
  useContext,
  useState,
  useEffect
} from 'react'
import PlayCircleOutline from '@material-ui/icons/PlayCircleOutline'
import { Image, Link, Text } from 'rebass'
import CircularProgress from '@material-ui/core/CircularProgress'
import {
  UneeqContext,
  useUneeqSpaceToTalk,
  useUneeqState
} from 'uneeq-react-core'
import { debounce, shuffle } from 'lodash'
// @ts-ignore
import marksy from 'marksy'
import DynamicForm from '../DynamicForm/DynamicForm'
import Form, { IconTypes } from '../DynamicForm/interfaces/Form'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { styles as s } from './styles'
import Button from '@material-ui/core/Button'
import CardActions from '@material-ui/core/CardActions'
import DeleteIcon from '@material-ui/icons/Delete'
import AddIcon from '@material-ui/icons/Add'
import DoneIcon from '@material-ui/icons/Done'
import ArrowForward from '@material-ui/icons/ArrowForward'
import ArrowBack from '@material-ui/icons/ArrowBack'
import { useIsSmallScreen } from 'uneeq-react-core'
import { Facebook, LinkedIn, Twitter } from '@material-ui/icons'
import {
  ThemeProvider,
  Typography,
  unstable_createMuiStrictModeTheme as createMuiTheme
} from '@material-ui/core'
import MUITheme from '../DynamicForm/theme'
import { ClassNameMap } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton
} from 'react-share'
import Video from '../Video'
import SuggestionsMenu from '../SuggestionsMenu'
import SubmitFormButton from '../DynamicForm/SubmitFormButton'
const headingStyles = {
  fontSize: 2,
  fontWeight: 'bold',
  m: 0,
  p: 0
}

const getThemeStyles: any = (theme: Theme) => ({
  appBar: {
    position: 'relative'
  },
  layout: {
    width: 'auto',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(2) * 2)]: {
      width: '100%',
      marginLeft: 'auto',
      marginRight: 'auto'
    }
  },

  paper: {
    flexGrow: 1,
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  button: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(1),
    cursor: 'pointer'
  },
  autocomplete: {
    flexGrow: 1
  },
  autoCompleteSuggestionsMenu: {
    marginTop: theme.spacing(1)
  },
  radioGroup: {},
  formControl: {
    flexGrow: 1,
    flexWrap: 'nowrap'
  },
  formControlLabel: {
    flexGrow: 1,
    flexBasis: '11em'
  },
  formSection: {
    alignItems: 'space-between'
  },
  formSectionActions: {
    justifyContent: 'flex-end'
  },
  utteranceButton: {
    label: 'utterance-button',
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: 0,
    marginBottom: 0,
    width: ['95%', '95%', '95%', 'auto', 'auto'],
    minWidth: '100px',
    flexGrow: 1,
    textAlign: 'left',
    justifyContent: 'space-between',
    color: 'inherit',
    fontWeight: 'normal',
    textTransform: 'inherit',
    cursor: 'pointer',
    fontSize: '1.3rem !important',
    '&:hover': {
      opacity: 0.8
    }
  }
})

const theme = createMuiTheme(MUITheme)
theme.palette.type = 'light'

const stripHTML = (html: string) => {
  let doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

export const getUtteranceFromURI = (uri: string) => {
  const match = uri.match(/^say\:(.*)$/)
  if (!match) {
    return undefined
  }
  return match[1]
}

const styles = s.informationContent
const shareButtonResetStyles = {
  backgroundColor: 'inherit !important',
  border: 'inherit !important',
  padding: 'inherit !important',
  font: 'inherit !important',
  cursor: 'inherit !important',
  color: `inherit !important`
}

const ShareButtons = () => {
  const { t } = useTranslation()
  return (
    <React.Fragment>
      <Typography variant="h5">{t('app:Share.description')}</Typography>
      <CardActions>
        <Button
          component={LinkedinShareButton}
          style={shareButtonResetStyles}
          title={t('app:Share.title')}
          summary={t('app:Share.summary')}
          url={t('app:Share.url')}
          color="secondary"
          variant="contained"
          startIcon={<LinkedIn />}
        >
          {t('app:Share.buttonLabel')}
        </Button>

        <Button
          component={FacebookShareButton}
          style={shareButtonResetStyles}
          color="secondary"
          variant="contained"
          startIcon={<Facebook />}
          url={t('app:Share.url')}
          hashtag={`#${t('app:Share.hashtag')}`}
          quote={`${t('app:Share.title')} ${t('app:Share.summary')}`}
        >
          {t('app:Share.buttonLabel')}
        </Button>

        <Button
          component={TwitterShareButton}
          style={shareButtonResetStyles}
          url={t('app:Share.url')}
          hashtags={['digitalhumans']}
          title={t('app:Share.title')}
          color="secondary"
          variant="contained"
          startIcon={<Twitter />}
        >
          {t('app:Share.buttonLabel')}
        </Button>
      </CardActions>
    </React.Fragment>
  )
}

const compile = (
  markdown: string,
  send: (text: string) => void,
  sendCallback: (text: string) => void,
  sending: boolean,
  getButtonIcon: (name: string) => JSX.Element | undefined,
  alternateCard: boolean,
  muiClasses: ClassNameMap,
  collapseExpanded: () => void
) => {
  const { submittingForm, submittedForm, transcript } = useUneeqState()
  const { config } = useContext(UneeqContext)
  const parser = marksy({
    components: {
      /*
        Use the following format in DialogFlow or other NLP service to add a button that sends an utterance
        when clicked to the UneeQ platform. You must wrap the component in ```marksy ``` otherwise it will
        fail to render when parsed by marksy, and will display as a text string instead:

        {
          "instructions": {
            "displayHtml": {
              "html": "{\"information\":\"# Choose an option: \\n\\n ```marksy\\nh(UtteranceButton, {className: \\\"back-btn\\\", utterance: \\\"no\\\"}, \\\"Back to Conversation\\\")\\n```\"}"
            }
          }
        }
      */

      UtteranceButton(props: any) {
        return (
          <Button
            className={muiClasses.utteranceButton}
            style={{ justifyContent: props.expanded || 'flex-start' }}
            key="submit"
            disabled={sending}
            size={props.size || 'large'}
            color={props.color || alternateCard ? 'inherit' : 'primary'}
            variant="text"
            startIcon={getButtonIcon(props.startIcon)}
            endIcon={getButtonIcon(props.endIcon)}
            onClick={event => {
              event.preventDefault()
              collapseExpanded()
              send(props.utterance)
              sendCallback(props.utterance)
            }}
          >
            {props.children}
          </Button>
        )
      },
      SubmitFormButton(props: any) {
        return (
          <SubmitFormButton
            muiClasses={muiClasses}
            size={props.size}
            color={props.color}
            alternateCard={false}
            startIcon={props.startIcon}
            endIcon={props.endIcon}
            additionalData={transcript}
            autoSubmit={config.autoSubmitForm}
          />
        )
      },
      ShareButton(props: any) {
        return <ShareButtons />
      },
      Video(props: any) {
        return (
          <Video
            item={{
              type: 'video',
              source: props.source,
              width: props.width,
              height: props.height
            }}
          ></Video>
        )
      },
      SuggestionsMenu(props: any) {
        if (!props.items) {
          return null
        }

        const suggestions = props.items.split('|')

        if (!suggestions) {
          return null
        }

        const items =
          props.randomPick !== undefined
            ? shuffle(suggestions).slice(0, props.randomPick + 1)
            : suggestions
        return (
          <SuggestionsMenu
            askHandler={send}
            items={items as any[]}
            helperText={props.helperText}
            inline={props.inline || true}
            subHeader={props.subHeader}
            visible={true}
            listOnly={props.listOnly}
            areRowsContained={false}
          ></SuggestionsMenu>
        )
      }
    },
    elements: {
      a: (props: any) => {
        const utterance = getUtteranceFromURI(props.href)
        return utterance ? (
          <Link
            {...props}
            rel="noopener noreferrer"
            onClick={(event: Event) => {
              event.preventDefault()
              send(utterance)
              sendCallback(utterance)
            }}
          />
        ) : (
          <Link {...props} rel="noopener noreferrer" />
        )
      },
      h1: ({ children }: any) => (
        <Typography
          variant="h5"
          style={{ display: 'inline-block', width: '85%' }}
        >
          {children}
        </Typography>
      ),
      h2: ({ children }: any) => (
        <Typography variant="h6">{children}</Typography>
      ),
      h3: ({ children }: any) => (
        <Typography variant="body1">{children}</Typography>
      ),
      h4: ({ children }: any) => (
        <Typography variant="body2">{children}</Typography>
      )
    },
    createElement
  })

  return parser(markdown, {})
}

interface HeadingInformation {
  type: 'heading'
  text: string
}
interface TextInformation {
  type: 'text'
  text: string
}
interface HTMLInformation {
  type: 'html'
  html: string
}
interface MarkdownInformation {
  type: 'markdown'
  markdown: string
}
interface ImageInformation {
  type: 'image'
  source: string
  label: string
  width: string
}
export interface VideoInformation {
  type: 'video'
  source: string
  width: string
  height: string
}
export interface LinkInformation {
  type: 'link'
  href: string
  label: string
}
interface FormInformation {
  type: 'form'
  schema: string
  fieldSuggestedResponses: any
  schemaData?: Form
}

export type InformationItemWithoutList =
  | HeadingInformation
  | TextInformation
  | HTMLInformation
  | ImageInformation
  | VideoInformation
  | LinkInformation
  | MarkdownInformation
  | FormInformation

interface ListInformation {
  type: 'list'
  items: InformationItemWithoutList[]
}

type InformationItem =
  | ListInformation
  | HeadingInformation
  | TextInformation
  | HTMLInformation
  | ImageInformation
  | VideoInformation
  | LinkInformation
  | MarkdownInformation
  | FormInformation

export const getButtonIcon = (name: string) => {
  switch (name) {
    case IconTypes.Delete:
      return <DeleteIcon />
    case IconTypes.Add:
      return <AddIcon />
    case IconTypes.Done:
      return <DoneIcon />
    case IconTypes.ArrowForward:
      return <ArrowForward />
    case IconTypes.ArrowBack:
      return <ArrowBack />
    case IconTypes.Play:
      return <PlayCircleOutline />
    case IconTypes.CircularProgress:
      return <CircularProgress />
    default:
      return
  }
}

const renderInformationItem = (
  item: InformationItem,
  index: number,
  alternateCard: boolean,
  sending: boolean,
  debouncedSend: any,
  muiClasses: ClassNameMap,
  dispatch: any,
  schemaData: any,
  expandedInfo: boolean,
  useStyles: () => void,
  setSchemaData: (responseData: any) => void,
  sendText: (utterance: string) => void
): any => {
  const collapseExpanded = () => dispatch({ type: 'collapseExpandedInfo' })

  switch (item.type) {
    case 'html':
      console.warn('HTML type used')
      return null //ReactHtmlParser(item.html)
    case 'text':
      return <Text key={index}>{item.text}</Text>
    case 'heading':
      return (
        <Text fontSize={3} key={index}>
          {item.text}
        </Text>
      )
    case 'markdown':
      const compiled = compile(
        stripHTML(item.markdown),
        debouncedSend,
        (text: string) => {
          dispatch({ type: 'suggestedResponseSent', payload: text })
        },
        sending,
        getButtonIcon,
        alternateCard,
        muiClasses,
        collapseExpanded
      )
      return compiled.tree
    case 'list':
      return (
        <ul key={index}>
          {item.items.map((child, index) =>
            renderInformationItem(
              child,
              index,
              alternateCard,
              sending,
              debouncedSend,
              muiClasses,
              dispatch,
              schemaData,
              expandedInfo,
              useStyles,
              setSchemaData,
              sendText
            )
          )}
        </ul>
      )
    case 'link':
      return (
        <Link href={item.href} rel="external" target="_blank" key={index}>
          <Text>{item.label}</Text>
        </Link>
      )
    case 'image':
      return <Image src={item.source} alt={item.label} key={index} />
    case 'video':
      return <Video key={index} item={item}></Video>
    case 'form':
      return (
        schemaData.layout.length > 0 && (
          <DynamicForm
            key="dynamic-form"
            schema={schemaData}
            paletteType="light"
            showTitle={false}
            useStyles={useStyles}
            gridSpacing={2}
            sendText={sendText}
            fieldSuggestedResponses={item.fieldSuggestedResponses}
          />
        )
      )
  }
}

interface InformationProps {
  information: InformationItem[]
}

const InformationContent: React.FC<InformationProps> = ({ information }) => {
  const { config } = useContext(UneeqContext)
  const { expandedInfo } = useUneeqState()
  const isSmallScreen = useIsSmallScreen()
  const alternateCard = !isSmallScreen && config.alternateOnScreenInfoCard
  const { sending } = useUneeqSpaceToTalk()
  const { sendText, dispatch } = useContext(UneeqContext)
  const debouncedSend = useRef(debounce((text: string) => sendText(text), 2000))
    .current
  const useStyles = makeStyles(getThemeStyles)
  const muiClasses = useStyles()

  return (
    <ThemeProvider theme={theme}>
      {information?.length > 0 &&
        information.map((item: InformationItem, index: number) => {
          const [schemaData, setSchemaData] = useState({
            title: '',
            layout: []
          } as Form)

          useEffect(() => {
            if (!(item as FormInformation).schema) {
              return
            }
            const fetchSchemaData = async (url: string) => {
              console.log('Loading form schema from: ', url)
              const response = await fetch(url)
              const responseData = await response.json()
              setSchemaData(responseData)
            }
            fetchSchemaData((item as FormInformation).schema)
          }, [item.type, (item as FormInformation).schema])

          return renderInformationItem(
            item,
            index,
            alternateCard,
            sending,
            debouncedSend,
            muiClasses,
            dispatch,
            schemaData,
            expandedInfo,
            useStyles,
            setSchemaData,
            sendText
          )
        })}
    </ThemeProvider>
  )
}

export default InformationContent
